import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api/client';
import { getActiveTenantId } from '@/lib/tenant';
import type {
  InviteMemberInput,
  Tenant,
  TenantMember,
  TenantMembership,
  TenantSignupInput,
} from '@/lib/api/types';

/** Tenants the current user belongs to (source of truth for the switcher). */
export function useMyTenants() {
  return useQuery({
    queryKey: ['tenants', 'mine'],
    queryFn: () => apiFetch<TenantMembership[]>('/tenants/mine'),
  });
}

export function useSignupTenant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TenantSignupInput) =>
      apiFetch<TenantMembership>('/tenants/signup', { method: 'POST', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tenants', 'mine'] });
    },
  });
}

export function useCurrentTenant() {
  return useQuery({
    queryKey: ['tenant', 'me', getActiveTenantId()],
    queryFn: () => apiFetch<Tenant>('/tenants/me'),
  });
}

export function useTenantMembers() {
  return useQuery({
    queryKey: ['tenant', 'members', getActiveTenantId()],
    queryFn: () => apiFetch<TenantMember[]>('/tenants/members'),
  });
}

export function useInviteMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: InviteMemberInput) =>
      apiFetch<TenantMember>('/tenants/members/invite', { method: 'POST', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tenant', 'members'] });
    },
  });
}
